import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Box,
    Container,
    createTheme,
    CssBaseline,
    LinearProgress,
    List,
    ListItem,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    ThemeProvider,
    Tooltip,
    Typography
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import {
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { each } from 'lodash';
import { SnackbarProvider } from "notistack";
import PubNub from 'pubnub';
import { PubNubProvider } from 'pubnub-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import AiGenerationStatus from '../../components/app/AiGenerationStatus';
import TeamInfoPopoverContext from '../../components/app/contexts/TeamInfoPopoverContext';
import HelpMenuButton from '../../components/app/HelpMenuButton';
import MaterialInfoPopover from '../../components/app/material-info-popover';
import { ResourcesDropdown } from '../../components/app/resources-dropdown';
import { SettingsDropdown } from '../../components/app/settings-dropdown';
import TeamDropdownBridge from '../../components/app/teams-dropdown-bridge';
import UploadStatusMenu from '../../components/app/UploadStatusMenu';
import MaterialAvatar from '../../components/general/MaterialAvatar';
import NotificationPopover from '../../components/general/NotificationPopover';
import { ADMIN_VALID_EMAIL, APP_ELEMENT_DIMENSIONS, COMPONENT_GLOBAL_CONFIG } from "../../constants";
import useInterviewPageOrgTreeVisibility from '../../hooks/useInterviewPageOrgTreeVisibility';
import { baseAtom, calculateSelectedGroup, canAccessScoringPage, checkRole, findChild, findParent, getChannels, orgtreeStickyAtom, orgTreeVisibleAtom } from '../../stores/jotai/base';
import { beaconStateAtom, setIsBeaconLoaded } from '../../stores/jotai/beacon';
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { selectedGroupAtom } from "../../stores/jotai/orgTree/selectedGroup";
import { teamsAtom } from "../../stores/jotai/teams/teams";
import { useGetNotifications } from '../../tanstackQuery/queries/notification';
import { useGetOrgTreeData } from '../../tanstackQuery/queries/org-tree';
import { useGetTeams } from "../../tanstackQuery/queries/teams";
import { isInPresentationMode } from '../../util';
import { beaconUtils } from '../../util/beaconUtils';
import MaterialChatContainer from '../chat/material-chat';
import MaterialOrgTree from '../orgtree/material-org-tree';
import MaterialOrgTreeToggle from '../orgtree/material-org-tree-toggle';
import PubnubListener from '../pubnub-listener';
import AppCreateButton from './AppCreateButton';
import { CustomRoute } from './custom-route';
import MaterialChatToggle from './material-chat-toggle';
import { containsAny } from './v3-app-container';
import ProgressExportStatus from "../../components/app/ProgressExportStatus";
import { STOCK_THEMES } from "../../util/stockThemes";
import { PostHogProvider} from 'posthog-js/react'
import { SAdminBaseAtom } from "../../stores/jotai/super-admin/superAdminBase";
import GroupModals from "../../components/super-admin/common/GroupModals";
import TemplateListModal from "../../components/discovery/template/templateListModal";
const Beacon = beaconUtils();

function MaterialAppLayout({ children }) {
    const [base, setBase] = useAtom(baseAtom);
    const location = useLocation()
    const navigate = useNavigate()
    const {keycloak}=useKeycloak();
    const theme = useTheme();
    const currentPath = location.pathname + location.search;
    const isAdminPannel=location?.pathname?.includes('/admin');

    const [beaconState, setBeaconState] = useAtom(beaconStateAtom);
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const isOrgtreeSticky = useAtomValue(orgtreeStickyAtom);
    const isOrgTreeVisible = useAtomValue(orgTreeVisibleAtom);
    const [orgTreeValue,setOrgTreeValue]=useState(null);
    const [isSuperAdmin,setIsSuperAdmin]=useState(false);
    const [sAdminBase,setSAdminBase]=useAtom(SAdminBaseAtom);
    const [parentGroupID,setParentGroupID] = useState("root")

    const [isTeamDropDownOpen, setIsTeamDropDownOpen] = useState(false);
    const [notification,setNotification] = useState([]);
    const [teams,setTeams]=useAtom(teamsAtom);
    const setSelectedGroupInfo = useSetAtom(selectedGroupAtom)
    const [adminOrgID, setAdminOrgID] = useState(null);

    const groupID=base?.team?.groupID;
    const orgID=base?.team?.orgID;
    const sessionID=discoverySession?.sessionID;

    const { data: orgTreeData, isLoading: isOrgTreeLoading } = useGetOrgTreeData(
        { accessToken: keycloak?.token },
        isAdminPannel 
            ? { orgID:adminOrgID } 
            : { groupID, orgID, sessionID },
        {
            enabled: isAdminPannel ? !!adminOrgID : !!(groupID && orgID && sessionID),
        }
    );

    useEffect(() => {
        if (isAdminPannel && sAdminBase?.selectedOrg?.orgID) {
            setAdminOrgID(sAdminBase?.selectedOrg?.orgID);
        }
        else{
            setSAdminBase((prev) => {
                const parentData = orgTreeData?.hierarchy ? findParent(base?.team?.groupID, orgTreeData.hierarchy) : null;
                const childData = orgTreeData?.hierarchy ? findChild(base?.team?.groupID, orgTreeData.hierarchy) : null;
        
                return {
                    ...prev,
                    selectedOrg: base.org,
                    selectedCohort: parentData,
                    selectedGroup: childData,
                };
            });
        }
    }, [isAdminPannel, sAdminBase?.selectedOrg, sAdminBase?.selectedCohort, sAdminBase?.selectedGroup]);

    const { data: notifications, isLoading } = useGetNotifications(
        { accessToken: keycloak?.token },
        { groupID, sessionID }, {
          enabled: !!groupID
        }
    );


    const {data:teamsData,isLoading:isTeamsLoading}=useGetTeams(
        { accessToken: keycloak?.token },
        { orgID, sessionID }, {
          enabled: !!(orgID && sessionID),
        }
    );

    const { isOrgTreeVisibleInInterviewPage,windowSize } =
        useInterviewPageOrgTreeVisibility();

    // TODO make this it's own atom to give it persistence on reload
    const [joyrideState, setJoyrideState] = useState({
        lightbulbOpen: false,
        activeJoyrideGuide: '',
        runningJoyride: false,
        joyrideStepIndex: 0,
        lightbulbMenuOpen: false,
        joyrideButtonClicked: false
    })

    const openTeamInfoPopover = useRef(undefined);
    const closeTeamInfoPopover = useRef(undefined);

    let orgTreeOpen = false;
    
    useEffect(()=>{
         if(orgTreeData && teamsData){
            setOrgTreeValue(orgTreeData);
            setBase((_base) => {
              let newBaseState = {
                ..._base,
              };
              return {
                ..._base,
                selectedGroup: calculateSelectedGroup(
                  newBaseState,
                  base?.team?.groupID,
                  orgTreeData?.flatHierarchy,
                  teamsData
                ),
              };
            });
            setSelectedGroupInfo({
                parentID: base.selectedGroup?.parentID,
                childID: base.selectedGroup?.childID,
            });
         }
    },[isOrgTreeLoading,isTeamsLoading])

    useEffect(()=>{
       if(teamsData){
         setTeams(teamsData);
       }
    },[teamsData,isTeamsLoading]);

    useEffect(()=>{
       const isSystemAdministrator = checkRole(base, 'isSystemAdministrator') && ADMIN_VALID_EMAIL.includes(base.user.email);
       if(isSystemAdministrator){
         setIsSuperAdmin(isSystemAdministrator)
       }
    },[base?.roleSnapshot]);

    try {
        orgTreeOpen = base?.app?.orgTreeOpen;
    } catch (e) { }

    // 11/14/23 don't show the org tree when the help panel is open
    try {
        if (base?.helpOpen === true) {
            orgTreeOpen = false;
        }
    } catch (e) { }

    const pageChange = (target) => {
        navigate(target)
    }

    let userID = undefined;
    let pubnubClient = undefined;

    try {
        userID = discoverySession?.user?.userID;
    } catch (e) { }

    try {
        pubnubClient = new PubNub({
            subscribeKey: `${process.env.NEXT_PUBLIC_PUBNUB_SUB_KEY}`,
            uuid: userID
        });
    }
    catch (e) { }

    useEffect(() => {
        if(notifications){
            setNotification(notifications)
        }  
    }, [notifications, isLoading]);

    const materialAppbar = () => {
        const appState = base?.app?.app;
        const isBaseLoading =  Object.keys(base?.user || {})?.length === 0 ;
        if (!appState) return null;
        
        const unreadChats = getChannels(base).reduce(
            (sum, channel) => sum + (base?.unread?.[channel?.key] || 0),
            0
        );
        let { cohort } = base;

        const isInstructor = checkRole(base, 'isInstructor');

        // If the user doesn't have a valid license then don't allow them
        // to access the chat
        let shouldChatBeDisabled = true;
        let chatIconOpacity = 0.5;

        if (appState.validLicense && !isInPresentationMode(currentPath)) {
            shouldChatBeDisabled = false;
            chatIconOpacity = 1;
        }

        const renderAppLinks = () => {
            const appState = base.app;
            const {  cohort, team, org } = base,
                //@ts-ignore
                appLinkList: ReactElement[] = [];

            let appLinks = [
                { to: '', icon: '', tooltip: 'Home', title: "Home" },
                { to: 'canvas', icon: 'view_quilt', tooltip: 'Canvas', title: "Canvas" },
                { to: 'discovery', icon: 'record_voice_over', tooltip: 'Interviews', title: "Interviews" },
                { to: 'insights', icon: 'lightbulb_outline', tooltip: 'Insights', title: "Insights" },
                { to: 'course-videos', icon: '', tooltip: 'Course Videos', title: 'Videos' },
            ];
            const isSystemAdministrator = checkRole(base,"isSystemAdministrator")  && ADMIN_VALID_EMAIL.includes(base.user.email);
            if(isSystemAdministrator){
                const adminRoute={ to: 'admin', icon: '', tooltip: 'Admin Panel', title: 'Admin' };
                appLinks.push(adminRoute); 
            }
           
            const currentLocation = currentPath ? currentPath: "/";
            let activeFoundLocation = -1;
            let i = 0;
            let appNavTabsHeight = '25px !important';

            each(appLinks, oLink => {
                if (oLink) {
                    const { to } = oLink;

                    // Start off assuming that we can navigate to any panel
                    // and that any panel is active
                    let isActive = true;
                    let cannotNav = false;

                    // If the location doesn't match our direction then assume it's not active
                    if (currentLocation !== '/' + to) {
                        isActive = false;
                    }

                    // Have a special case for secondary info hub types
                    if (!isActive && to === 'information-hub' && currentLocation === '/team-information-hub') {
                        isActive = true;
                    }

                    // If we are in a cohort yet not on in a team based group then we can only
                    // navigate to the dashboard and admin
                    if (cohort && !team) {
                        cannotNav = true;

                        // It's safe to navigate to these tabs
                        if (to.includes("admin") || to.includes("dashboard")) {
                            cannotNav = false;
                        }
                    }

                    // Check to see if we have an active license
                    // if we don't then all nav options should be disabled
                    if (appState.validLicense === false) {
                        //isActive = false;
                        cannotNav = true;
                    }

                    // If the org is fetching then we also cannot nav
                    if (org.isFetching) {
                        //isActive = false;
                        cannotNav = true;
                    }

                    const extendedRoutesCheck=currentLocation?.split('/');
                    if(extendedRoutesCheck && extendedRoutesCheck?.length>2 && extendedRoutesCheck[1]===to){
                        isActive=true;
                    }

                    if (isActive) {
                        activeFoundLocation = i;
                    }

                    appLinkList.push(
                        <Tab
                            key={`tab-to-${to}`}
                            sx={{
                                height: appNavTabsHeight,
                                minHeight: appNavTabsHeight,
                                maxHeight: appNavTabsHeight,
                                lineHeight: appNavTabsHeight,
                                minWidth: "auto",
                                padding: "0 16px !important",
                            }}
                            id={`joy-app-nav-${to}`}
                            onClick={() => {
                                pageChange("/" + to);
                            }}
                            disabled={cannotNav}
                            label={oLink?.title}
                        />
                    );

                    i++;
                }
            });
            return <Tabs className='app-root-navigation' sx={{ height: appNavTabsHeight, minHeight: appNavTabsHeight, maxHeight: appNavTabsHeight, lineHeight: appNavTabsHeight }} value={(activeFoundLocation === -1) ? false: activeFoundLocation} onChange={(e, t) => {
                //@ts-ignore
                pageChange('/' + appLinks[t].to);
            }}>
                {appLinkList}
            </Tabs>

            // return appLinkList;
        }

        let navLinks =  renderAppLinks();

        const v3TestGuide = [
            {
                target: 'body',
                title: (<Typography sx={{}}>Welcome to Innovation Within’s Discovery Platform!</Typography>),
                content: (<>
                    <Typography sx={{}}>We’re so excited you’re here, and we can’t wait to see what you create!</Typography>
                    <Typography sx={{}}>Behind this quick walk-through is our latest upgrade, including a speedier, more polished UI! It’s the same structure but cleaned up for a more intuitive flow. We’ve added a handful of NEW features, too.</Typography>
                    <Typography sx={{}}>Let’s show you around!</Typography>
                    <button onClick={() => setJoyrideState({ ...joyrideState, joyrideStepIndex: v3TestGuide?.length - 1, joyrideButtonClicked: true })}>Click here to go to the last step of the guide</button>
                </>),
                placement: 'center',
                disableBeacon: true,
                styles: { tooltip: { width: '650px' }, tooltipContent: { width: '100%', } },
                subGuides: [1, 2, 3, 4],
                buttonClicked: base?.joyrideButtonClicked,
            },
            {
                target: '#joy-app-mini-logo',
                title: (<Typography></Typography>),
                // title: "Click on our logo to access a new menu",
                content: (<Typography>Click on Our Logo to Access a New Menu</Typography>),
                disableOverlayClose: true,
                hideCloseButton: true,
                hideFooter: true,
                spotlightClicks: true,
                styles: { tooltip: { width: '266px' } },
            },
            {
                target: '#joy-app-lightbulb-menu',
                title: (<Typography>Click on our logo for help</Typography>),
                content: (<List sx={{ padding: '0px 0px 0px 0px' }}>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* You can find in-app guides for each page and feature in the platform</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* The Knowledge Base contains articles full of tips for approaching customer discovery (for example, creating hypotheses and interviewing)</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* <i>Something about feature suggestions</i></Typography></ListItem>
                </List>),
                placement: 'right',
                spotlightClicks: false,
            },
            {
                target: 'body',
                title: (<Typography>Our New Features Include the Following:</Typography>),
                content: (<List sx={{ padding: '0px 0px 0px 0px' }}>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* Custom Canvases (set by your instructors)</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* Custom Categories/Insights</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* New Insights Page</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* Bulk Interview Uploader, Including Videos!</Typography></ListItem>
                    <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography>* Transcription Editor Upgrades</Typography></ListItem>
                </List>),
                placement: 'center',
                styles: { tooltip: { width: '600px' }, tooltipContent: { width: '100%', } },
            },
        ]

        const renderAnalyticsDropdowns = () => {

            const isActive = containsAny(currentPath, [
                'dashboard'
            ]);

            let menuConfiguration: any = [];

            menuConfiguration = [
                ...menuConfiguration,
                {
                    value: 'dashboard?t=0',
                    label: 'Cohort Stats',
                    roles: "*"
                },
                {
                    value: 'dashboard?t=1',
                    label: 'Team Stats',
                    roles: "*"
                },
                {
                    value: 'dashboard?t=2',
                    label: 'Instructor Stats',
                    roles: "instructor"
                },
                {
                    value: 'dashboard?t=3',
                    label: 'Video Views',
                    roles: "*"
                }
            ]

            return <span id="joy-app-analytics" style={{ marginLeft: '0px', paddingLeft: '10px', paddingRight: '10px' }} className={isActive ? "resources-active" : "resources"}>
                <ResourcesDropdown
                    config={menuConfiguration}
                    user={base?.user}
                    org={base?.org}
                    app={base?.app}
                    itemWidth={"90px"}
                    controlWidth="auto"
                    menuTitle={"Analytics"}
                    currentPage={currentPath}
                    handleAction={(slug: string) => {
                        //@ts-ignore
                        pageChange('/' + slug)
                    }}
                    theme={theme}
                />
            </span>
        }

        const renderSettingsMenuMaterial = () => {
            const appState = base.app;
            
            const {
                user,
                org, selectedGroup
            } = base;

            return <SettingsDropdown
                user={user}
                org={org}
                setBase={setBase}
                app={appState}
                team={base?.team}
                base={base}
                cohort={base?.cohort}
                group={selectedGroup}
                theme={theme}
                avatar={
                    <Tooltip title="Account Settings & More">
                        <Stack direction="row" alignItems="center" sx={{
                            ":hover": {
                                ".MuiSvgIcon-root": {
                                    color: theme?.custom?.mainButtonHighlight,
                                }
                            }
                        }}>
                            <MaterialAvatar user={user} />
                            <ExpandMoreIcon sx={{ color: theme?.custom?.mainIconColor }} />
                        </Stack>
                    </Tooltip>
                }
                handleAction={(slug) => {
                    const {
                        team,
                        cohort
                    } = base;

                    // MP-1084
                    // If just the cohort is selected fall back to it, although the team
                    // should be the focus
                    // const cohortID = team ? team.id : cohort.id;

                    // Check for any situations where it's not just the route we should navigate to
                    if (slug === "profile") {
                        window.open("https://login.innovationwithin.services/realms/discovery/account/", "_blank");
                        return;
                    }

                    if (slug === "notification-settings") {

                        return;
                    }

                    if (slug === "signout") {
                        try {
                            localStorage.clear();
                        } catch (e) { }

                        try {
                            sessionStorage.clear();
                        } catch (e) { }

                        //appActions.flagLogOut();
                        window.location.href = 'https://login.innovationwithin.services/realms/discovery/protocol/openid-connect/logout';

                        return;
                    }

                    if (slug === "team-members") {
                        if (openTeamInfoPopover) {
                            openTeamInfoPopover.current({ activeTabIndex: 2 });
                        }

                        return;
                    }

                    // Fallback to just navigating to the slug
                    //@ts-ignore
                    pageChange('/' + slug);
                }}
            />
        }

        const renderUserContextBadge = () => {
            let { cohort, team } = base;

            let type: any = "";
            let label: any = "";

            if (cohort) {
                label = cohort.name;
                type = "Cohort: ";
            }

            if (team) {
                label = team.name;
                type = "Team: ";
            }


            const menuConfig: Array<{label: string, value: string}> = []

            const fH = orgTreeValue!==null ? orgTreeData?.flatHierarchy : undefined;

            if (fH !== undefined && teamsData!==undefined) {
                for (let i = 0; i < fH?.length; i++) {
                    const {
                        childID,
                        parentID
                    } = fH[i];
                    if (parentID === cohort?.groupID && teamsData[childID]) {
                        let shouldReturn = false;
                        const {isAdmin ,isCohortAdmin, isInstructor, isObserver, isSystemAdministrator}=base?.roleSnapshot;

                        orgTreeValue?.roles.forEach(role => {
                            if (!shouldReturn && role?.groupID === teamsData[childID]?.groupID) {
                                shouldReturn = true;
                            }
                        });
            
                        if(isAdmin || isCohortAdmin || isInstructor || isObserver || isSystemAdministrator){
                            shouldReturn=true;   
                        }

                        if(shouldReturn && childID && teamsData[childID]?.name){
                            menuConfig.push({
                                value: childID,
                                label: teamsData[childID]?.name
                            })
                        }
                    }
                }
            }

            menuConfig.sort((a, b) => a.label.localeCompare(b.label))

            let teamDropdownBridge = (
              <span id="joy-app-team-selector">
                <TeamDropdownBridge
                  echoOpen={(target) => {
                    setIsTeamDropDownOpen(target);
                  }}
                  config={menuConfig}
                  user={base.user}
                  org={base.org}
                  app={base.app}
                  itemWidth={"90px"}
                  menuTitle={
                    isBaseLoading ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        {type}
                        <Skeleton
                          variant="rounded"
                          height={22}
                          width={150}
                          sx={{ backgroundColor: "#EBEBEB", width: "100%" }}
                        />
                      </Stack>
                    ) : (
                      type + label
                    )
                  }
                />
              </span>
            );

            return teamDropdownBridge;
        }


        const renderResourcesDropdowns = () => {
            const hasAccessToScoringPage = canAccessScoringPage(base);

            const isActive = containsAny(currentPath, [
                'presentation', 'information-hub'
            ]);

            let menuConfiguration = [
                {
                    value: "information-hub?t=0",
                    label: "Cohort Info Hub",
                    roles: "*",
                },
                {
                    value: "information-hub?t=1",
                    label: "Team Info Hub",
                    roles: "*",
                },
                ...(!!hasAccessToScoringPage
                    ? [
                            {
                                value: "presentation",
                                label: "Scoring",
                                roles: "*",
                            },
                        ]
                    : []),
                // Hiding this tab temporarily
                {
                    value: "information-hub?t=2",
                    label: "Presentation Links",
                    roles: "*",
                },
            ];

            return <span id="joy-app-resources" style={{ marginLeft: '0px', paddingLeft: '10px', paddingRight: '10px' }} className={isActive ? "resources-active" : "resources"}>
                <ResourcesDropdown
                    config={menuConfiguration}
                    user={base.user}
                    org={base.org}
                    app={base.app}
                    itemWidth={"90px"}
                    controlWidth="auto"
                    menuTitle={"Resources"}
                    currentPage={currentPath}
                    handleAction={(slug: string) => {
                        // Fallback to just navigating to the slug
                        //@ts-ignore
                        pageChange('/' + slug)
                    }}
                    theme={theme}
                />
            </span>
        }

        const renderOrgTreeOpen = () => {
            if (orgTreeOpen) {
                return (
                  <MaterialOrgTreeToggle
                    menuIconButtonStyle={{
                      ":hover": {
                        transform: `scale(${theme.custom.iconScaleFactor})`,
                      },
                    }}
                    disabled={!isOrgTreeVisibleInInterviewPage}
                    tooltipText={`Hide Cohort Teams`}
                  />
                );
            }
            return (
                <MaterialOrgTreeToggle
                    menuIconButtonStyle={{
                        transform: "rotate(180deg)",
                        ":hover": {
                            transform: `rotate(180deg) scale(${theme?.custom?.iconScaleFactor})`,
                        },
                    }}
                    disabled={!isOrgTreeVisibleInInterviewPage}
                    tooltipText={`View Organization Tree.`}
                />
            );
        }

        return (
            <Stack
                sx={{
                    flexDirection: "row",
                    display: "flex",
                    padding: "0px 10px",
                    backgroundColor: theme?.custom?.white,
                    height: "70px",
                    borderBottom: `1px solid ${theme?.custom?.borderGrey}`,
                }}
            >
                <Stack
                    sx={{ display: "flex", flexDirection: "column" }}
                    justifyContent="space-between"
                >
                    <Box sx={{ display: "flex", marginTop: "4px", alignItems: "center"  }}>
                        {renderOrgTreeOpen()}
                        {renderUserContextBadge()}
                        <MaterialInfoPopover
                            cohort={base?.cohort}
                            team={base?.team}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ height: "25px" }}
                    >
                        {navLinks}
                        {!isInstructor && renderAnalyticsDropdowns()}
                        {cohort && cohort?.id ? renderResourcesDropdowns() : <span />}
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "auto",
                    }}
                    direction="row"
                    spacing={1}
                >
                    {base?.uppyFiles?.length > 0 && (
                        <UploadStatusMenu sx={{ marginRight: "10px !important" }} />
                    )}
                    <AppCreateButton />
                    <HelpMenuButton />
                    {renderSettingsMenuMaterial()}
                    <NotificationPopover notifications={notification}/>
                    <MaterialChatToggle
                        badgeClass="appbar-nav-item-badge white-badge-text"
                        unreadChats={unreadChats}
                        shouldChatBeDisabled={shouldChatBeDisabled}
                    />
                </Stack>
            </Stack>
        );
    }

    const renderChatPanel = () => {
        return <MaterialChatContainer {...base} pubState={base?.pubStore ? base?.pubStore : {}} />
    }

    useEffect(() => {
        Beacon.on("ready", () => {
            setIsBeaconLoaded(setBeaconState, true);
        });
    }, []);


    useEffect(() => {
        const { name, email } = base.user;
        const { isBeaconLoaded } = beaconState;

        if (isBeaconLoaded && !!email) {
            Beacon.identify({
                name,
                email,
            });
        }
    }, [base?.user, beaconState?.isBeaconLoaded]);

    useEffect(() => {
        const beaconId = process.env.NEXT_PUBLIC_BEACON_ID;

        if (Beacon.info()?.beaconId !== beaconId) {
            Beacon.init(process.env.NEXT_PUBLIC_BEACON_ID);
        }
    }, []);

    if (pubnubClient === undefined) {
        return <LinearProgress />
    }

    return (
      <TeamInfoPopoverContext.Provider
        value={{
          openTeamInfoPopover,
          closeTeamInfoPopover
        }}
      >
        <PubNubProvider client={pubnubClient}>
          <SnackbarProvider maxSnack={5}>
            <Container
              sx={{
                height: "100%",
                position: "relative",
                maxWidth: "100% !important"
              }}
              disableGutters={true}
            >
              <Stack flexDirection={"row"} sx={{ height: "100%" }}>
                <MaterialOrgTree
                  {...base}
                  isSuperAdmin={isSuperAdmin}
                  orgTreeData={orgTreeData}
                  windowSize={windowSize}
                  isAdminPannel={isAdminPannel}
                  setParentGroupID={setParentGroupID}
                />

                {/* TODO V3.1, use this to make some better mobile breakpoints */}
                <Box
                  sx={{
                    display: "flex",
                    overflow: "clip",
                    flexDirection: "column",
                    flex: "1",
                    width:
                      isOrgTreeVisible && isOrgtreeSticky
                        ? "calc(100% - 300px)"
                        : "100%"
                  }}
                >
                  <Stack direction="column">
                    <Box
                      id="quick-app-location-selector"
                      sx={{
                        position: "absolute",
                        top: "5px",
                        left: "1px",
                        width: "350px",
                        flexDirection: "row",
                        display: "flex"
                      }}
                    ></Box>
                    <Box id="appbar" sx={{ flexShrink: "1", height: "70px" }}>
                      {materialAppbar()}
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      height: "calc(100% - 71px)",
                      width: "100%"
                    }}
                  >
                    <Box
                      sx={{
                        flex: "1",
                        height: "100%",
                        width: `calc(100% - ${APP_ELEMENT_DIMENSIONS.CHAT_PANEL.WIDTH})`,
                        minWidth: "700px"
                      }}
                    >
                      {children}
                      <GroupModals parentGroupID={parentGroupID} />
                      <TemplateListModal/>
                    </Box>
                    {renderChatPanel()}
                  </Box>
                </Box>
              </Stack>
              <PubnubListener />
              <AiGenerationStatus />
              <ProgressExportStatus />
              <Toaster richColors position="bottom-left" />
            </Container>
          </SnackbarProvider>
        </PubNubProvider>
      </TeamInfoPopoverContext.Provider>
    );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      structuralSharing: false
    }
  }
});

export default function Layout({ children }) {
    // because this is generic page you can access user settings info here
    //@ts-ignore
    const activeTheme = createTheme(STOCK_THEMES.v3);

    /**
       * @description Posthog integration for better log.
       * @date 2025-03-13
    */
    const options = {
        api_host: "https://us.i.posthog.com",
    }
    const isProd = process.env.NEXT_PUBLIC_NODE_ENV === "prod";

    const innerContent=(
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={activeTheme}>
              <CssBaseline />
              <MaterialAppLayout>
                <CustomRoute />
                {children}
              </MaterialAppLayout>
            </ThemeProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </BrowserRouter>
    );

    return (
      <React.Fragment>
        {isProd ? (
          <PostHogProvider
            apiKey="phc_W4ryhlenp4OARUQNFafASv2FZGTU9NlVCwSkZ4xl78Q"
            options={options}
          >
            {innerContent}
          </PostHogProvider>
        ) : (
          innerContent
        )}
      </React.Fragment>
    );
}